import type { ReactNode } from 'react';
import type { InvoiceStatus, SchoolStatus, ServiceState } from '@eskoolhub/types';
import { IconClose } from './Icons';

// ── Chips ───────────────────────────────────────────────────────────────────

type ChipTone = 'success' | 'warn' | 'danger' | 'neutral';

export function Chip({ tone, children }: { tone: ChipTone; children: ReactNode }) {
  return <span className={`chip chip--${tone}`}>{children}</span>;
}

/**
 * Semantic colour mapping from the design brief §2 — active/paid = leaf,
 * overdue/suspended = coral, trial/attention = marigold.
 */
export function SchoolStatusChip({ status, trialDaysLeft }: { status: SchoolStatus; trialDaysLeft?: number | null }) {
  switch (status) {
    case 'ACTIVE':
      return <Chip tone="success">Active</Chip>;
    case 'TRIAL':
      return (
        <Chip tone={trialDaysLeft !== null && trialDaysLeft !== undefined && trialDaysLeft <= 5 ? 'danger' : 'warn'}>
          {trialDaysLeft !== null && trialDaysLeft !== undefined ? `Trial · ${trialDaysLeft}d` : 'Trial'}
        </Chip>
      );
    case 'PAYMENT_ISSUE':
      return <Chip tone="warn">Payment issue</Chip>;
    case 'SUSPENDED':
      return <Chip tone="danger">Suspended</Chip>;
    case 'ONBOARDING':
      return <Chip tone="neutral">Onboarding</Chip>;
    case 'CHURNED':
      return <Chip tone="neutral">Churned</Chip>;
  }
}

export function InvoiceStatusChip({ status, overdueDays }: { status: InvoiceStatus; overdueDays?: number | null }) {
  switch (status) {
    case 'PAID':
      return <Chip tone="success">Paid</Chip>;
    case 'OVERDUE':
      return <Chip tone="danger">{overdueDays ? `Overdue ${overdueDays}d` : 'Overdue'}</Chip>;
    case 'SENT':
      return <Chip tone="warn">Sent</Chip>;
    case 'DRAFT':
      return <Chip tone="neutral">Draft</Chip>;
    case 'VOID':
      return <Chip tone="neutral">Void</Chip>;
  }
}

export const SERVICE_STATE_COLOUR: Record<ServiceState, string> = {
  OPERATIONAL: 'var(--leaf)',
  DEGRADED: 'var(--marigold-deep)',
  DOWN: 'var(--coral)',
};

export function StatusDot({ state }: { state: ServiceState }) {
  return <span className="dot" style={{ background: SERVICE_STATE_COLOUR[state] }} />;
}

// ── Tiles ───────────────────────────────────────────────────────────────────

export function StatTile({
  label,
  value,
  caption,
  tone,
  dark,
}: {
  label: string;
  value: string | number;
  caption?: string | null;
  tone?: 'success' | 'danger' | 'warn';
  dark?: boolean;
}) {
  const colour =
    tone === 'success' ? 'var(--leaf)' : tone === 'danger' ? 'var(--coral)' : tone === 'warn' ? 'var(--marigold-deep)' : undefined;

  return (
    <div className={dark ? 'tile tile--dark' : 'tile'}>
      <div className="label">{label}</div>
      <div className="tile__value" style={colour ? { color: colour } : undefined}>
        {value}
      </div>
      {caption ? <div className="caption">{caption}</div> : null}
    </div>
  );
}

export function SchoolTile({ initials, primary }: { initials: string; primary?: boolean }) {
  return <div className={primary ? 'school-tile school-tile--primary' : 'school-tile'}>{initials}</div>;
}

// ── Modal ───────────────────────────────────────────────────────────────────

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={wide ? 'modal modal--wide' : 'modal'}>
        <div className="modal__head">
          <div className="modal__title">{title}</div>
          <button className="modal__close" onClick={onClose} aria-label="Close">
            <IconClose fill="var(--ink-soft)" />
          </button>
        </div>
        {children}
        {footer ? <div className="modal__foot">{footer}</div> : null}
      </div>
    </div>
  );
}

// ── States ──────────────────────────────────────────────────────────────────

export function EmptyState({ title, message, action }: { title: string; message: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty__art">{title}</div>
      <div>{message}</div>
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="empty">
      <div className="banner banner--error">{message}</div>
      {onRetry ? (
        <button className="btn btn--ghost btn--sm" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function SkeletonTiles({ count = 4 }: { count?: number }) {
  return (
    <div className="grid-4">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="tile">
          <div className="skeleton" style={{ height: 12, width: '60%' }} />
          <div className="skeleton" style={{ height: 30, width: '40%' }} />
          <div className="skeleton" style={{ height: 12, width: '75%' }} />
        </div>
      ))}
    </div>
  );
}

export function SkeletonRows({ count = 4 }: { count?: number }) {
  return (
    <div className="card" style={{ gap: 12 }}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 46 }} />
      ))}
    </div>
  );
}

// ── Toggle ──────────────────────────────────────────────────────────────────

export function Switch({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      className="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    />
  );
}
